import { useBrowsePresetApi } from '@aibees/shared/api';
import {
  CategoryKey,
  OptionType,
  TemplateCategory,
  TemplateOption,
} from '@aibees/shared/features';
import { useMemo } from 'react';
import { useTemplateContext } from '../context/TemplateContext';
import { TransformedData } from '../types';
import { useDefaultTemplateData } from './use-default-template-data';
import { useExportTemplateDataType } from './use-export-template-data-type';

export function useConvertDataToExportSetting() {
  const { selectedTemplate, selectedPreset } = useTemplateContext();
  const dataType = useExportTemplateDataType();
  const defaultTemplateData = useDefaultTemplateData();

  const { data } = useBrowsePresetApi<TemplateCategory[]>({
    name: selectedTemplate?.name,
    size: 25,
    type: dataType as string,
  });

  const items: TemplateCategory[] | undefined = useMemo(() => {
    const isStoredDefault = (
      value: unknown
    ): value is { default: true; items?: TemplateCategory[] } =>
      typeof value === 'object' &&
      value !== null &&
      (value as { default?: boolean }).default === true;

    if (
      selectedTemplate?.name ||
      selectedPreset?.value === selectedTemplate?.id
    ) {
      const selected = data?.content.find(
        (x) => x.name === selectedTemplate?.name
      );
      // The stored default preset may be from an older version of the
      // template. Skip it so defaultTemplateData (the in-code constant) is used.
      if (isStoredDefault(selected?.value)) return undefined;
      return selected?.value as TemplateCategory[] | undefined;
    }
    const selectedPresetData = data?.content.find(
      (x) => x.id === selectedPreset?.value
    );
    if (isStoredDefault(selectedPresetData?.value)) return undefined;
    return selectedPresetData?.value as TemplateCategory[] | undefined;
  }, [
    data?.content,
    selectedPreset?.value,
    selectedTemplate?.id,
    selectedTemplate?.name,
  ]);

  // const items: TemplateCategory[] =
  //   data?.content[0]?.value && (data?.content[0]?.value as any).default
  //     ? (data?.content[0]?.value as any)?.items
  //     : data?.content[0]?.value;

  const prepareDataForServer = useMemo(() => {
    return items?.reduce<TransformedData>((acc, item) => {
      if (item.isSingleEntry && item.entries[0]) {
        let fields = item.entries[0].fields as unknown as OptionType[];
        // Filter out COMPANY_URL from experiences category before sending to server
        // This field is available in the template configuration but is not supported
        // by the backend API for experiences export. Removing it prevents server-side
        // errors and ensures only valid export fields are sent.
        if (item.key === CategoryKey.experiences) {
          fields = fields.filter((field) => field !== OptionType.COMPANY_URL);
        }
        acc[item.key] = { fields: fields as unknown as TemplateOption[] };
      } else if (!item.isSingleEntry) {
        acc[item.key] = item.entries.map((entry, index) => {
          let fields = entry.fields as unknown as OptionType[];
          // Filter out COMPANY_URL from experiences category before sending to server
          // This field is available in the template configuration but is not supported
          // by the backend API for experiences export. Removing it prevents server-side
          // errors and ensures only valid export fields are sent.
          if (item.key === CategoryKey.experiences) {
            fields = fields.filter((field) => field !== OptionType.COMPANY_URL);
          }
          return {
            index: entry.index ?? index,
            fields: fields as unknown as TemplateOption[],
          };
        });
      }
      return acc;
    }, {});
  }, [items]);
  return prepareDataForServer && Object.keys(prepareDataForServer).length > 0
    ? prepareDataForServer
    : defaultTemplateData;
}
